import axios from 'axios';
import { VITE_ENV_REVIEW } from '@/app/data/general_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import axiosRetry from 'axios-retry';
import { ZIGGU_API_URL } from '@/app/util/urls';
import _ from 'lodash-es';
import eventBus from '@/plugins/event_bus';
import { ROUTES_WITH_SUBDOMAIN_REGEX } from '@/app/data/route_constants.js';

let storeRef;

const getStore = () => storeRef;

const api = axios.create({
  baseURL: ZIGGU_API_URL,
});

function set_request_header(headers, name, value) {
  if (!value) return headers;
  if (headers && typeof headers.set === 'function') {
    headers.set(name, value);
    return headers;
  }
  const headers_object = headers || {};
  headers_object[name] = value;
  return headers_object;
}

function sanitize_q_sort(config_params) {
  if (!config_params || config_params['q[s]'] === undefined) return;

  const is_valid = (v) => typeof v === 'string' && !/^(null|undefined)\b/.test(v.trim());
  const value = config_params['q[s]'];

  if (Array.isArray(value)) {
    const filtered = value.filter(is_valid);
    if (filtered.length) config_params['q[s]'] = filtered;
    else delete config_params['q[s]'];
  } else if (!is_valid(value)) {
    delete config_params['q[s]'];
  }
}

function get_response_header(headers, name) {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] ?? headers[name.toLowerCase()];
}

// https://github.com/softonic/axios-retry/issues/87
const retryDelay = (retryNumber = 0) => {
  const seconds = 2 ** retryNumber * 1000;
  const randomMs = 1000 * Math.random();
  return seconds + randomMs;
};

axiosRetry(api, {
  retries: 3,
  retryDelay,
});

function validate_constraints(config) {
  // disable manipulative actions by employees during impersonation of customer
  const store = getStore();
  if (!store) return;
  const { current_is_impersonation } = store.getters;
  const { current_profile_true } = store.getters;
  if (
    current_is_impersonation &&
    current_profile_true &&
    current_profile_true.type === PROFILE_TYPES.EMPLOYEE &&
    config.method !== 'get'
  ) {
    const error = new Error('Forbidden action during impersonate.');
    error.sentry_discard = true;
    throw error;
  }
}

// WARNING: should be kept in sync with useUppyUpload.js component as well (outside of axios' scope)
api.interceptors.request.use(
  (config) => {
    validate_constraints(config);
    // create a new object off of config.params
    let config_params = { ...config.params };
    // add referer header with full URL (request.referer only contains base URL due to SPA nature of app)
    config.headers = set_request_header(config.headers, 'X-Referer-SPA', window.location.href);
    // add CSRF token header from similarly named cookie
    const csrf_cookie = document.cookie.split('; ').find((row) => row.startsWith('X-CSRF-Token='));
    const csrf_token = csrf_cookie ? csrf_cookie.split('=').slice(1).join('=') : null;
    config.headers = set_request_header(config.headers, 'X-CSRF-Token', csrf_token);
    // append (relevant) meta params to request's parameters
    // (1) add 'subdomain' meta param to uniquely identify tenant
    let _subdomain = null;
    const parts = window.location.host.split('.');
    const is_review = import.meta.env.VITE_ENV === VITE_ENV_REVIEW && !import.meta.env.IS_LOCAL;
    const is_subdomain = (!is_review && parts.length > 2) || (is_review && parts.length > 3);
    if (is_subdomain) {
      [_subdomain] = parts;
    } else if (ROUTES_WITH_SUBDOMAIN_REGEX.test(window.location.pathname)) {
      const match = window.location.pathname.match(/^\/[^/]+\/([^/]+)/);
      _subdomain = match ? match[1] : null;
    }
    if (_subdomain) {
      config_params = Object.assign(config_params || {}, { _subdomain });
    }
    // (2) profile impersonation, add '_profile_id' and '_profile_type' meta params
    const _profile_id = sessionStorage.getItem('_profile_id');
    const _profile_type = sessionStorage.getItem('_profile_type');
    if (_profile_id && _profile_type) {
      config_params = Object.assign(config_params || {}, { _profile_id, _profile_type });
    }

    if (
      config_params &&
      config_params.parameters &&
      typeof config_params.parameters === 'object' &&
      !Array.isArray(config_params.parameters)
    ) {
      config_params.parameters = JSON.stringify(config_params.parameters);
    }
    sanitize_q_sort(config_params);
    config.params = config_params;
    return config;
  },
  (error) => {
    throw error;
  },
);

// Response interceptor for handling API responses and errors
api.interceptors.response.use(
  (response) => {
    // verify cache integrity by comparing "current" object between API (via custom HTTP header) and APP (via vuex)
    const store = getStore();
    if (!store) return response;
    const current_integrity = get_response_header(response.headers, 'current-integrity');
    if (!current_integrity) return response;
    const timestamps_api = _.chain(current_integrity)
      .split(',')
      .map((timestamp) => [timestamp.split('=')[0], parseInt(timestamp.split('=')[1], 10)])
      .fromPairs()
      .value();
    const timestamps_app = _.chain(store.getters.current_integrity)
      .split(',')
      .map((timestamp) => [timestamp.split('=')[0], parseInt(timestamp.split('=')[1], 10)])
      .fromPairs()
      .value();
    if (timestamps_app['client.updated_at'] < timestamps_api['client.updated_at']) {
      eventBus.$emit('fetchClient');
      eventBus.$emit('fetchFeatureToggles');
    }
    if (timestamps_app['profile.notified_at'] < timestamps_api['profile.notified_at']) {
      eventBus.$emit('fetchProfile');
      eventBus.$emit('fetchNotificationSubgroups');
    } else if (timestamps_app['profile.updated_at'] < timestamps_api['profile.updated_at']) {
      eventBus.$emit('fetchProfile');
      eventBus.$emit('fetchFeatureToggles');
    }
    return response;
  },
  (error) => {
    // ignore/bypass errors from login page, to be handled by login page itself
    if (window.location.pathname === '/login') {
      throw error;
    }

    // let app handle unauthorized errors
    if (error.response !== undefined && error.response.status === 401) {
      eventBus.$emit('errors:401');
      throw error;
      // let app handle forbidden errors but do propagate error to Sentry (as this shouldn't happen)
    } else if (error.response !== undefined && error.response.status === 403) {
      eventBus.$emit('errors:403');
      throw error;
      // error which likely indicates access was revoked to a resource
    } else if (error.response !== undefined && error.response.status === 404) {
      if (error.config && error.config.skip_404_handler) {
        throw error;
      }
      eventBus.$emit('errors:404');
    } else if (error.response !== undefined && error.response.status === 422) {
      if (error.config && error.config.skip_global_error_handler) {
        throw error;
      }
      eventBus.$emit('errors:422', error.response.data.messages);
      // employee -> customer impersonation triggering a manipulative (forbidden) action
    } else if (error.message && error.message === 'Forbidden action during impersonate.') {
      eventBus.$emit('errors:418');
      // error without a response can e.g. be an unauthorized preflight OPTION request
    } else {
      // propagate other errors (to Sentry)
      throw error;
    }
  },
);
export const setApiStore = (store) => {
  storeRef = store;
};
export default api;
